import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data.data;
          localStorage.setItem('access_token', accessToken);
          localStorage.setItem('refresh_token', newRefreshToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (username: string, password: string) =>
    api.post('/auth/login', { username, password }),

  logout: () => api.post('/auth/logout'),

  logoutAll: () => api.post('/auth/logout-all'),

  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),

  getMe: () => api.get('/auth/me'),

  updateProfile: (data: { email?: string; first_name?: string; last_name?: string }) =>
    api.put('/auth/me', data),

  changePassword: (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => api.post('/auth/change-password', data),

  getSessions: () => api.get('/auth/sessions'),
};

// Credentials API
export const credentialsApi = {
  getAll: () => api.get('/credentials'),
  getById: (id: string) => api.get(`/credentials/${id}`),
  getTypes: () => api.get('/credentials/types'),
  setData: (id: string, data: Record<string, string>) =>
    api.put(`/credentials/${id}/data`, { data }),
};

// Users API (admin only)
export const usersApi = {
  getAll: (page = 1, limit = 50) => api.get('/users', { params: { page, limit } }),
  create: (data: {
    username: string;
    email: string;
    password: string;
    role: string;
    first_name?: string;
    last_name?: string;
  }) => api.post('/users', data),
  update: (id: string, data: { role?: string; is_active?: boolean }) =>
    api.put(`/users/${id}`, data),
  remove: (id: string) => api.delete(`/users/${id}`),
};

// Workflows API
export const workflowsApi = {
  getAll: () => api.get('/workflows'),
  getOverview: () => api.get('/workflows/overview'),
  getExecutions: (id: string, page = 1, limit = 25) =>
    api.get(`/workflows/${id}/executions`, { params: { page, limit } }),
};

export default api;
